"use client"

import { ArrowRight } from "lucide-react"
import { Link } from "next-view-transitions"
import Image from "next/image"
import { useSearchParams } from "next/navigation"

import { buildRedirectUrl } from "@/lib/new-url"

import Footer from "@/components/nango/en/layout/footer"
import MessageDialogLanding from "@/components/nango/tsp/message-dialog-landing"
import TspNavbar from "@/components/nango/tsp/navbar"
import Testimonials from "@/components/nango/tsp/testimonials"
import { Button } from "@/components/ui/button"
import VideoDialog from "@/components/ui/video-dialog"

import { TSP_CONFIG } from "./config"
import { useIsMobile } from "@/hooks/use-mobile"

const HOME_JSON_LD = {
  "@context": "https://schema.org",
  "@graph": [
    {
      "@type": "Organization",
      name: TSP_CONFIG.SITE_NAME,
      url: TSP_CONFIG.BASE_URL,
      logo: `${TSP_CONFIG.BASE_URL}${TSP_CONFIG.SITE_CONFIG.ogImage.replace(/^\//, "")}`,
      description: TSP_CONFIG.SITE_DESCRIPTION,
    },
    {
      "@type": "WebSite",
      name: TSP_CONFIG.SITE_NAME,
      url: TSP_CONFIG.BASE_URL,
      description: TSP_CONFIG.SITE_DESCRIPTION,
    },
  ],
}

export default function Home() {
  const isMobile = useIsMobile()
  const redirect = useSearchParams().get("redirect")
  const loginHref = redirect ? buildRedirectUrl("/login", redirect) : "/login"
  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(HOME_JSON_LD) }}
      />
      <TspNavbar />
      <main className="container flex flex-col items-center">
        <div className="min-h-screen w-full">
          <div className="relative flex flex-col items-center justify-start sm:items-start sm:py-16">
            <div className="flex h-[60dvh] flex-col justify-center gap-8 text-center sm:gap-16 sm:text-start lg:h-[70dvh]">
              <div className="flex flex-col">
                <h1 className="text-2xl tracking-tight sm:text-4xl lg:text-6xl">
                  Learn from your <span className="font-bold">results</span>,
                  <br />
                  prepare for the <span className="font-bold">future</span>.
                </h1>
                <p className="text-muted-foreground mt-4 max-w-sm text-xs sm:text-sm md:max-w-md lg:text-base">
                  This website aims to help mogul{" "}
                  <span className="font-bold">athletes</span> and{" "}
                  <span className="font-bold">coaches</span> prepare for the
                  2026 winter Olympics, by providing customized data-driven
                  result analysis.
                </p>
              </div>
              <div className="flex w-full flex-col items-center justify-center gap-2 sm:flex-row sm:justify-start">
                <MessageDialogLanding />
                <Link href={loginHref}>
                  <Button
                    size={isMobile ? "lg" : "xl"}
                    className="sm:text-md group"
                  >
                    Try for free!
                    <ArrowRight
                      className="ms-2 -me-1 transition-transform group-hover:translate-x-0.5"
                      size={16}
                      strokeWidth={2}
                      aria-hidden="true"
                    />
                  </Button>
                </Link>
              </div>
            </div>
            {isMobile ? (
              <Image
                height={1000}
                width={1000}
                src="/tsp/mateo-landing.png"
                alt="hero-section"
                className="-z-10 rounded-lg object-cover"
                style={{
                  maskImage: `linear-gradient(0deg, rgba(255,255,255,0) 0%, rgba(255,255,255,0.9) 80%)`,
                }}
              />
            ) : (
              <Image
                fill
                src="/tsp/mateo-landing.png"
                alt="hero-section"
                className="-z-10 rounded-lg object-cover"
                style={{
                  maskImage: `linear-gradient(90deg, rgba(255,255,255,0) 0%, rgba(255,255,255,0.9) 80%)`,
                }}
              />
            )}
          </div>
        </div>
        <section id="testimonials">
          <Testimonials />
        </section>
        <section id="athletes" className="py-24 sm:py-32">
          <div className="grid place-items-center lg:grid-cols-2 lg:gap-24">
            <div>
              <h2 className="text-primary text-md mb-2 tracking-wider sm:text-lg">
                For athletes
              </h2>
              <h2 className="mb-4 text-xl font-bold sm:text-3xl md:text-4xl">
                Learn from each result
              </h2>
              <ul className="text-muted-foreground mb-8 text-justify text-xs sm:text-sm">
                <li>
                  &#8226; Use Twister Spread to analyse each{" "}
                  <strong>race</strong>, and figure out what could have been
                  improved.
                </li>
                <li>
                  &#8226; Go over each season of your <strong>career</strong>,
                  to find your strong suits, and your weaknesses.
                </li>
                <li>
                  &#8226; Study your <strong>jumps</strong>, to gain insights
                  into which ones you should perform.
                </li>
              </ul>
            </div>
            <VideoDialog
              animationStyle="from-center"
              videoSrc="https://www.youtube.com/embed/TmjfvtLBpMA?si=aPM6AOBSSDwzNRnz&autoplay=1"
              thumbnailSrc="/tsp/youtube-athlete-thumbnail.png"
              thumbnailAlt="Athlete demo"
            />
          </div>
        </section>
        <section id="coach" className="py-24 sm:py-32">
          <div className="grid place-items-center lg:grid-cols-2 lg:gap-24">
            <VideoDialog
              className="order-last lg:order-1"
              animationStyle="from-center"
              videoSrc="https://www.youtube.com/embed/4L9gh2K5mL8?si=ms7cpN_s_ZO56ddc&autoplay=1"
              thumbnailSrc="/tsp/youtube-coach-thumbnail.png"
              thumbnailAlt="Coach demo"
            />
            <div className="order-2">
              <h2 className="text-primary text-md mb-2 tracking-wider sm:text-lg">
                For coaches
              </h2>
              <h2 className="mb-4 text-xl font-bold sm:text-3xl md:text-4xl">
                Bring your athletes to new heights
              </h2>
              <ul className="text-muted-foreground mb-8 text-justify text-xs sm:text-sm">
                <li>
                  &#8226; Use all the <strong>athlete tools</strong> to advise
                  your team with data driven analysis.
                </li>
                <li>
                  &#8226; Study the <strong>global trends</strong> to stay ahead
                  of the curve when it comes to jump selection.
                </li>
                <li>
                  &#8226; Analyse <strong>judge bias</strong>, to optimise each
                  athlete according to the judging panel.
                </li>
              </ul>
            </div>
          </div>
        </section>
      </main>
      <Footer config={TSP_CONFIG} />
    </>
  )
}
